import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Plus, TrendingUp, Wallet, Users, HandHeart, Sparkles, MapPin, ArrowRight, Heart, HandCoins, ShieldCheck, FileDown, Trash2, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import CreateCagnotteWizard from '../components/CreateCagnotteWizard';
import BiometricEnrollCard from '../components/BiometricEnrollCard';
import { Button } from '../components/ui/button';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { jsPDF } from 'jspdf';

function downloadWithdrawalReceipt(w, user) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  pdf.setFillColor(14, 58, 58);
  pdf.rect(0, 0, 210, 34, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('FodindinCagnotte', 15, 15);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Reçu de retrait de cagnotte', 15, 23);
  pdf.setTextColor(30, 30, 30);
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Détails du retrait', 15, 48);
  pdf.setDrawColor(217, 119, 87);
  pdf.setLineWidth(0.8);
  pdf.line(15, 51, 60, 51);
  const rows = [
    ['Cagnotte', w.cagnotteTitle || '-'],
    ['Bénéficiaire', user?.name || '-'],
    ['Montant', `${new Intl.NumberFormat('fr-FR').format(w.amount || 0)} FCFA`],
    ['Opérateur', (w.provider || 'Mobile Money').toUpperCase()],
    ['Vérification', w.verification || '-'],
    ['Date', w.createdAt ? new Date(w.createdAt).toLocaleString('fr-FR') : '-'],
    ['Référence', w.id],
    ['Statut', w.status === 'mocked' ? 'Virement simulé (test)' : 'Virement effectué'],
  ];
  let y = 62;
  pdf.setFontSize(10.5);
  rows.forEach(([k, v]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${k} :`, 15, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(String(v), 55, y, { maxWidth: 140 });
    y += 9;
  });
  pdf.setFontSize(8.5);
  pdf.setTextColor(120, 120, 120);
  pdf.text('Document généré automatiquement par FodindinCagnotte. Identité du bénéficiaire vérifiée', 15, y + 8);
  pdf.text('par authentification forte (biométrie passkey ou code OTP).', 15, y + 13);
  pdf.save(`recu-retrait-${(w.cagnotteTitle || 'cagnotte').slice(0, 20).replace(/\s+/g, '-')}.pdf`);
}

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

const withdrawalStatus = (s) => {
  const v = (s || '').toLowerCase();
  if (['success', 'completed', 'sent', 'val'].includes(v)) return { label: 'Réussi', cls: 'bg-emerald-100 text-emerald-700' };
  if (['failed', 'rejected', 'refused', 'error'].includes(v)) return { label: 'Échoué', cls: 'bg-red-100 text-red-600' };
  if (v === 'mocked') return { label: 'Simulé', cls: 'bg-neutral-100 text-neutral-500' };
  return { label: 'En cours', cls: 'bg-amber-100 text-amber-700' };
};

function StatCard({ Icon, label, value, color }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 card-lift">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 text-[12px] uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
      <div className="mt-1 text-[26px] font-semibold text-[#1a1d1a] tracking-tight">{value}</div>
    </div>
  );
}

function CagnotteRow({ c, onDelete }) {
  const isYako = c.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  const pct = Math.min(100, Math.round(((c.raised || 0) / (c.goal || 1)) * 100));
  const canDelete = (c.raised || 0) === 0 || c.status === 'withdrawn';
  const lockReason = (c.raised || 0) >= (c.goal || 0)
    ? "Retirez d'abord les fonds collectés"
    : 'Des dons ont déjà été reçus';
  return (
    <Link to={`/cagnotte/${c.id}`} className="group flex gap-4 rounded-2xl bg-white border border-black/5 p-4 card-lift">
      <div className="w-24 h-24 rounded-xl overflow-hidden bg-neutral-100 shrink-0">
        <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white" style={{ background: accent }}>{c.type}</span>
          {c.status === 'withdrawn' && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-neutral-200 text-neutral-600">Fonds retirés</span>
          )}
          <span className="text-[11.5px] text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.country}</span>
          <button
            type="button"
            data-testid={`delete-cagnotte-btn-${c.id}`}
            title={canDelete ? 'Supprimer cette cagnotte' : `Suppression verrouillée : ${lockReason}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canDelete) onDelete(c); }}
            className={`ml-auto p-1.5 rounded-full transition-colors ${canDelete ? 'text-neutral-400 hover:text-red-600 hover:bg-red-50' : 'text-neutral-300 cursor-not-allowed'}`}
          >
            {canDelete ? <Trash2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          </button>
        </div>
        <h3 className="mt-1 font-semibold text-[15px] text-[#1a1d1a] truncate">{c.title}</h3>
        <div className="mt-2 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[12px] text-neutral-600">
          <span><span className="font-semibold text-neutral-900">{formatN(c.raised || 0)}</span> / {formatN(c.goal)} FCFA</span>
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.donors || 0}</span>
        </div>
      </div>
    </Link>
  );
}

function ConfirmDeleteModal({ target, busy, onCancel, onConfirm }) {
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
      <div className="w-full max-w-[400px] rounded-3xl bg-white p-6 text-center shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center bg-red-50 text-red-600">
          <Trash2 className="w-6 h-6" />
        </div>
        <h3 className="mt-4 text-[17px] font-semibold text-[#1a1d1a]">Supprimer cette cagnotte ?</h3>
        <p className="mt-2 text-[13px] text-neutral-600">« {target.title} » et ses données liées (dons, commentaires) seront définitivement supprimées. Cette action est irréversible.</p>
        <div className="mt-5 flex gap-2">
          <button type="button" data-testid="delete-cancel-btn" onClick={onCancel} className="flex-1 h-11 rounded-full bg-[#f5f4ef] text-[13.5px] font-medium text-neutral-700 hover:bg-neutral-200 transition-colors">Annuler</button>
          <button type="button" data-testid="delete-confirm-btn" onClick={onConfirm} disabled={busy} className="flex-1 h-11 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-[13.5px] font-medium flex items-center justify-center gap-1.5 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

function DonationRow({ d }) {
  const cag = d.cagnotte;
  if (!cag) return null;
  const isYako = cag.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  return (
    <Link to={cag.id ? `/cagnotte/${cag.id}` : '#'} className="flex items-center gap-3 rounded-xl bg-white border border-black/5 p-3.5 hover:bg-neutral-50 transition-colors">
      <div className="w-11 h-11 rounded-lg overflow-hidden bg-neutral-100 shrink-0">
        {cag.image ? <img src={cag.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: accent }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-[#1a1d1a] truncate">{cag.title}</div>
        <div className="text-[11.5px] text-neutral-500">{new Date(d.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[14px] font-semibold" style={{ color: accent }}>{formatN(d.amount)} FCFA</div>
        <div className="text-[11px] text-neutral-500 capitalize">{d.provider}</div>
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const { user, loading: authLoading, setAuthOpen, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cagnottes, setCagnottes] = useState([]);
  const [donations, setDonations] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [accountDeleteOpen, setAccountDeleteOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState('Joyeuse');

  useEffect(() => {
    if (!authLoading && !user) { setAuthOpen(true); }
  }, [authLoading, user, setAuthOpen]);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [s, c, d, w] = await Promise.all([
          api.get('/stats/mine'),
          api.get('/cagnottes/mine'),
          api.get('/donations/mine'),
          api.get('/withdrawals/mine').catch(() => ({ data: { items: [] } })),
        ]);
        if (!alive) return;
        setStats(s.data);
        setCagnottes(c.data.items || []);
        setDonations(d.data.items || []);
        setWithdrawals(w.data.items || []);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('[dashboard]', e);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user]);

  const openCreate = (type = 'Joyeuse') => { setWizardType(type); setWizardOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/cagnottes/${deleteTarget.id}`);
      setCagnottes((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast({ title: 'Cagnotte supprimée', description: `« ${deleteTarget.title} » a été supprimée définitivement.` });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Suppression refusée', description: e?.response?.data?.detail || 'Erreur lors de la suppression.' });
    } finally { setDeleting(false); }
  };

  const confirmDeleteAccount = async () => {
    if (deletingAccount) return;
    setDeletingAccount(true);
    try {
      await api.delete('/auth/me');
      toast({ title: 'Compte supprimé', description: 'Vos données personnelles ont été effacées. À bientôt.' });
      setAccountDeleteOpen(false);
      logout();
      navigate('/');
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || "Impossible de supprimer le compte." });
    } finally { setDeletingAccount(false); }
  };

  if (authLoading) {
    return (
      <div>
        <Header onCreate={openCreate} />
        <div className="max-w-[1000px] mx-auto px-4 py-24 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <Header onCreate={openCreate} />
        <div className="max-w-[600px] mx-auto px-4 py-24 text-center">
          <h1 className="text-[24px] font-semibold text-[#1a1d1a]">Connexion requise</h1>
          <p className="mt-2 text-neutral-600">Connectez-vous pour accéder à votre tableau de bord.</p>
          <Button onClick={() => setAuthOpen(true)} className="mt-6 rounded-full bg-[#1a1d1a] hover:bg-black text-white h-11 px-5">Se connecter</Button>
        </div>
        <AuthModal />
      </div>
    );
  }

  return (
    <div>
      <Header onCreate={openCreate} />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-neutral-500">Tableau de bord</div>
            <h1 className="mt-2 text-[clamp(1.8rem,3vw,2.6rem)] font-semibold tracking-tight text-[#1a1d1a]">
              Bonjour <span className="serif-italic text-[#d97757]">{user.name || 'ami·e'}</span>
            </h1>
            <p className="mt-1 text-[14px] text-neutral-600">Voici l'activité de vos cagnottes et de vos dons.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openCreate('Joyeuse')} className="btn-anim rounded-full bg-[#d97757] hover:bg-[#c66745] text-white h-10 px-4 text-[13.5px]">
              <Plus className="w-4 h-4 mr-1" /> Cagnotte Joyeuse
            </Button>
            <Button onClick={() => openCreate('Yako')} className="btn-anim rounded-full bg-[#5a8268] hover:bg-[#4a6f57] text-white h-10 px-4 text-[13.5px]">
              <HandHeart className="w-4 h-4 mr-1" /> Cagnotte Yako
            </Button>
          </div>
        </div>

        {loading || !stats ? (
          <div className="mt-8 text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline-block text-neutral-500" /></div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard Icon={Sparkles} label="Cagnottes créées" value={stats.ownedCount} color="#d97757" />
              <StatCard Icon={TrendingUp} label="Actives" value={stats.activeCount} color="#5a8268" />
              <StatCard Icon={Wallet} label="Total collecté" value={`${formatN(stats.totalRaised)} FCFA`} color="#0e3a3a" />
              <StatCard Icon={Heart} label="Dons effectués" value={`${formatN(stats.donationsTotal)} FCFA`} color="#d97757" />
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Vos cagnottes</h2>
                  <span className="text-[12px] text-neutral-500">{cagnottes.length} au total</span>
                </div>
                {cagnottes.length === 0 ? (
                  <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-8 text-center">
                    <p className="text-[14px] text-neutral-600">Vous n'avez pas encore de cagnotte.</p>
                    <Button onClick={() => openCreate('Joyeuse')} className="mt-4 rounded-full bg-[#1a1d1a] hover:bg-black text-white h-10 px-4">
                      <Plus className="w-4 h-4 mr-1" /> Créer ma première cagnotte
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {cagnottes.map((c) => <CagnotteRow key={c.id} c={c} onDelete={setDeleteTarget} />)}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-baseline justify-between">
                  <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Vos dons</h2>
                  <span className="text-[12px] text-neutral-500">{stats.donationsMade}</span>
                </div>
                {donations.length === 0 ? (
                  <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 p-6 text-center">
                    <p className="text-[13px] text-neutral-600">Aucun don pour l'instant. Explorez les cagnottes pour soutenir un proche.</p>
                    <Link to="/#cagnottes" className="mt-3 inline-flex items-center gap-1 text-[13px] text-[#d97757] font-medium hover:gap-2 transition-all">Voir les cagnottes <ArrowRight className="w-3.5 h-3.5" /></Link>
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    {donations.slice(0, 20).map((d) => <DonationRow key={d.id} d={d} />)}
                  </div>
                )}
              </div>
            </div>

            {withdrawals.length > 0 && (
              <div className="mt-10" data-testid="withdrawals-history-section">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Historique des retraits</h2>
                  <span className="text-[12px] text-neutral-500">{withdrawals.length}</span>
                </div>
                <div className="mt-4 rounded-2xl bg-white border border-black/5 overflow-hidden">
                  {withdrawals.map((w) => (
                    <div key={w.id} className="flex items-center gap-4 px-5 py-4 border-b border-black/5 last:border-0" data-testid={`withdrawal-row-${w.id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#128C4A]/10 text-[#128C4A] flex items-center justify-center shrink-0">
                        <HandCoins className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[13.5px] text-neutral-900 truncate">{w.cagnotteTitle}</div>
                        <div className="text-[11.5px] text-neutral-500 flex items-center gap-1.5 flex-wrap">
                          {w.createdAt ? new Date(w.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#5a8268]" /> {w.verification}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[14px] font-semibold text-[#1a1d1a]">{formatN(w.amount)} FCFA</div>
                        <div className="text-[11px] text-neutral-500 capitalize flex items-center justify-end gap-1.5">
                          {w.provider || 'Mobile Money'}
                          <span data-testid={`withdrawal-status-${w.id}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold normal-case ${withdrawalStatus(w.status).cls}`}>
                            {withdrawalStatus(w.status).label}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`withdrawal-receipt-btn-${w.id}`}
                        onClick={() => downloadWithdrawalReceipt(w, user)}
                        className="shrink-0 inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1.5 rounded-full bg-[#0e3a3a]/5 text-[#0e3a3a] hover:bg-[#0e3a3a]/10 transition-colors"
                      >
                        <FileDown className="w-3.5 h-3.5" /> Reçu PDF
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 grid lg:grid-cols-[1fr_1fr] gap-5">
              <BiometricEnrollCard />
              <div className="rounded-2xl bg-[#f5f4ef] border border-black/5 p-6 sm:p-7">
                <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-[#5a8268]">Sécurité renforcée</div>
                <h3 className="mt-2 text-[19px] font-semibold text-[#1a1d1a] leading-tight">Pourquoi enrôler une <span className="serif-italic text-[#d97757]">passkey</span> ?</h3>
                <ul className="mt-4 space-y-2.5 text-[13px] text-neutral-700 leading-relaxed">
                  <li className="flex gap-2"><span className="text-[#5a8268]">•</span> Un attaquant qui obtiendrait votre code OTP ne pourrait rien faire sans votre empreinte / Face ID.</li>
                  <li className="flex gap-2"><span className="text-[#5a8268]">•</span> Aucune biométrie ne quitte votre appareil — Fodindin ne stocke qu'une clé publique.</li>
                  <li className="flex gap-2"><span className="text-[#5a8268]">•</span> Compatible Touch ID, Face ID, Windows Hello, Android biometric.</li>
                  <li className="flex gap-2"><span className="text-[#5a8268]">•</span> Vous pouvez enrôler plusieurs appareils et supprimer un appareil perdu à tout moment.</li>
                </ul>
              </div>
            </div>

            <div className="mt-10 rounded-2xl bg-white border border-red-200/70 p-6 sm:p-7" data-testid="account-danger-zone">
              <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-red-500">Zone sensible · RGPD</div>
              <h3 className="mt-2 text-[19px] font-semibold text-[#1a1d1a]">Supprimer mon compte</h3>
              <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed max-w-[560px]">
                Conformément à votre droit à l'effacement, vous pouvez supprimer définitivement votre compte et vos données personnelles (nom, téléphone, documents). Vos dons passés seront anonymisés. Cette action est <span className="font-semibold">irréversible</span>.
              </p>
              <Button
                type="button"
                data-testid="delete-account-btn"
                onClick={() => setAccountDeleteOpen(true)}
                className="btn-anim mt-4 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white text-[13px] px-5"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement mon compte
              </Button>
            </div>
          </>
        )}
      </div>

      {accountDeleteOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="account-delete-modal">
          <div className="w-full max-w-[440px] rounded-2xl bg-white p-6 sm:p-7">
            <h3 className="text-[19px] font-semibold text-[#1a1d1a]">Confirmer la suppression</h3>
            <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">
              Toutes vos données personnelles seront effacées et vous serez déconnecté. Cette action ne peut pas être annulée. Voulez-vous continuer ?
            </p>
            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" data-testid="account-delete-cancel-btn" onClick={() => setAccountDeleteOpen(false)} disabled={deletingAccount}
                className="h-10 px-4 rounded-full text-[13px] font-medium text-neutral-700 hover:bg-neutral-100">Annuler</button>
              <Button type="button" data-testid="account-delete-confirm-btn" onClick={confirmDeleteAccount} disabled={deletingAccount}
                className="h-10 px-5 rounded-full bg-red-600 hover:bg-red-700 text-white text-[13px]">
                {deletingAccount ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Oui, supprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <AuthModal />
      <ConfirmDeleteModal target={deleteTarget} busy={deleting} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
      <CreateCagnotteWizard open={wizardOpen} onOpenChange={setWizardOpen} initialType={wizardType} onCreated={(c) => c?.id && navigate(`/cagnotte/${c.id}`)} />
    </div>
  );
}
